import { ApolloError, gql } from "@apollo/client";
import { forwardRef } from "@chakra-ui/react";
import { Form, useFormikContext } from "formik";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormTemplateSettings } from "src/hooks/useFormTemplateSettings";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSelectedSchoolsByFormId } from "src/hooks/useSelectedSchoolsByFormId";
import {
  ADDRESS_ANSWERS_FRAGMENT,
  CUSTOM_QUESTION_ANSWERS_FRAGMENT,
  FORM_ANSWER_FRAGMENT,
  FORM_DISCLAIMER_FRAGMENT,
  GRADES_ANSWERS_FRAGMENT,
} from "src/operations/fragments/form";
import { FORM_TEMPLATE_FRAGMENT } from "src/operations/fragments/formTemplate";
import { EligibilityServiceProvider } from "src/services/eligibility/EligibilityServiceProvider";
import * as FormTemplate from "src/services/formTemplate";
import {
  Answer,
  getAllQuestionsAndAnswers,
} from "src/services/formTemplate/answer";
import * as Question from "src/services/formTemplate/question";
import {
  getFormCompleteQuestions,
  hasOptions,
} from "src/services/formTemplate/question";
import * as Visibility from "src/services/formTemplate/visibility";
import * as AF from "src/types/formTemplate";
import { FormTemplateSettings } from "src/types/formTemplate/v2";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
export type { VisibilityContext } from "src/services/formTemplate/visibility";

type key = string;
type value = string;

export type FormProviderFormTemplate =
  GQL.GetFormProviderFormTemplate_form_by_pk_form_template;

export type FormTemplateContext = {
  gql: FormProviderFormTemplate;
  formTemplateSettings: FormTemplateSettings;
  formTemplate: AF.FormTemplate<AF.WithId>;
  questionKeysMap: Map<key, uuid>; // Map of question.key to question.id
  questionIdsMap: Map<uuid, key>; // Map of question.id to question.key
  optionsMap: Map<uuid, value>; // Map of option.id to option.value
};

export type FormContext = {
  status: GQL.form_status_enum;
  gql: GQL.GetFormProviderForm;
  formAnswersMap: FormAnswersMap;
  selectedSchoolsSet: Set<string>;
  updateAnswers: (values: Record<string, Answer>) => void;
  refetchAnswers: () => Promise<unknown>;
  refetchSelectedSchools: () => Promise<unknown>;
  questionsWithVerifiedAnswers: Set<uuid>;
};

export type FormAnswersMap = Map<string, Answer>;

const FormTemplateCtx = createContext<
  RD.RemoteData<Error, FormTemplateContext>
>(RD.notAsked());

const FormCtx = createContext<RD.RemoteData<Error, FormContext>>(RD.notAsked());

type Props = {
  formId: string;
  children: (
    formTemplateContextRD: RD.RemoteData<Error, FormTemplateContext>,
    formContextRD: RD.RemoteData<Error, FormContext>,
    formTemplateSettingsRD: RD.RemoteData<Error, FormTemplateSettings>
  ) => React.ReactNode;
};

export const FormProvider: FC<Props> = ({ formId, children }) => {
  const formTemplateQuery = useRemoteDataQuery<
    GQL.GetFormProviderFormTemplate,
    GQL.GetFormProviderFormTemplateVariables
  >(
    gql`
      ${FORM_TEMPLATE_FRAGMENT}
      query GetFormProviderFormTemplate($formId: uuid!) {
        form_by_pk(id: $formId) {
          form_template {
            enrollment_period {
              active
              name
            }
            ...FormTemplateFragment
          }
        }
      }
    `,
    {
      variables: { formId },
      fetchPolicy: "no-cache",
    }
  );

  const formQuery = useRemoteDataQuery<
    GQL.GetFormProviderForm,
    GQL.GetFormProviderFormVariables
  >(
    gql`
      ${FORM_ANSWER_FRAGMENT}
      ${GRADES_ANSWERS_FRAGMENT}
      ${ADDRESS_ANSWERS_FRAGMENT}
      ${CUSTOM_QUESTION_ANSWERS_FRAGMENT}
      ${FORM_DISCLAIMER_FRAGMENT}

      query GetFormProviderForm($formId: uuid!) {
        form_by_pk(id: $formId) {
          status
        }
        form_answer(where: { form_id: { _eq: $formId } }) {
          ...FormAnswerFragment
        }
        grades_answer(where: { form_id: { _eq: $formId } }) {
          ...GradesAnswersFragment
        }
        form_address(where: { form_id: { _eq: $formId } }) {
          ...AddressAnswersFragment
        }
        custom_question_answer(where: { form_id: { _eq: $formId } }) {
          ...CustomQuestionAnswersFragment
        }
        form_disclaimer(where: { form_id: { _eq: $formId } }) {
          ...FormDisclaimerFragment
        }

        form_verification_result(where: { form_id: { _eq: $formId } }) {
          form_verification_id
          verification_status
        }
      }
    `,
    { variables: { formId }, fetchPolicy: "network-only" }
  );

  const formTemplateId = formTemplateQuery.remoteData
    .map((gql) => gql.form_by_pk?.form_template?.id ?? "")
    .withDefault("");

  const formTemplateSettingsValue = useFormTemplateSettings(formTemplateId);

  const selectedSchoolsQuery = useSelectedSchoolsByFormId(formId);

  const formTemplateValue = useMemo(() => {
    return RD.toTuple(formTemplateQuery.remoteData, formTemplateSettingsValue)
      .mapError(
        (error) =>
          new Error("Error loading form template and settings", {
            cause: error,
          })
      )
      .andThen(
        ([gql, formTemplateSettings]): RD.RemoteData<
          Error,
          FormTemplateContext
        > => {
          const formTemplateGQL = gql.form_by_pk?.form_template;
          if (formTemplateGQL === undefined) {
            return RD.failure(new Error("Invalid formId"));
          }
          const formTemplate = FormTemplate.fromGQL(formTemplateGQL);
          const questionKeysMap = new Map<key, uuid>();
          const questionIdsMap = new Map<uuid, key>();
          const optionsMap = new Map<uuid, key>();
          const formQuestions = getFormCompleteQuestions(formTemplate);
          for (const question of formQuestions) {
            if (question.key) {
              questionKeysMap.set(question.key, question.id);
              questionIdsMap.set(question.id, question.key);
            }

            if (hasOptions(question)) {
              for (const option of question.options) {
                if (option.value) {
                  optionsMap.set(option.id, option.value);
                }
              }
            }

            if (question.type === AF.GradesType) {
              for (const option of question.options) {
                optionsMap.set(option.id, option.value ?? option.label);
              }
            }
          }
          return RD.success({
            gql: formTemplateGQL,
            formTemplateSettings: formTemplateSettings,
            formTemplate,
            questionKeysMap,
            questionIdsMap,
            optionsMap,
          });
        }
      );
  }, [formTemplateQuery.remoteData, formTemplateSettingsValue]);
  const [formAnswersMap, setFormAnswersMap] = useState<
    RD.RemoteData<Error, FormAnswersMap>
  >(RD.notAsked());

  useEffect(() => {
    setFormAnswersMap(
      RD.toTuple(formQuery.remoteData.mapError(toError), formTemplateValue).map(
        ([answers, { formTemplate }]) => toAnswersMap(answers, formTemplate)
      )
    );
  }, [formQuery, formTemplateValue]);

  const updateAnswers = useCallback(
    (newAnswers: Record<uuid, Answer>) => {
      setFormAnswersMap((rd) => {
        return rd.map((map) => {
          const updatedMap = new Map(map);
          Object.entries(newAnswers).forEach(([id, answer]) => {
            updatedMap.set(id, answer);
          });
          return updatedMap;
        });
      });
    },
    [setFormAnswersMap]
  );

  const formValue: RD.RemoteData<Error, FormContext> = useMemo(() => {
    return RD.toTuple4(
      formAnswersMap,
      formQuery.remoteData,
      selectedSchoolsQuery.remoteData,
      formTemplateValue
    ).andThen(
      ([formAnswersMap, formGQL, selectedSchools, formTemplateContext]) => {
        if (!formGQL.form_by_pk) {
          return RD.failure(new Error("Invalid formId"));
        }

        const refetchAnswers = formQuery.refetch;
        const selectedSchoolsSet = new Set(
          selectedSchools.map((school) => school.id)
        );
        const refetchSelectedSchools = selectedSchoolsQuery.refetch;
        const questionsWithVerifiedAnswers = getQuestionsWithVerifiedAnswers(
          formTemplateContext,
          formGQL
        );
        return RD.success({
          gql: formGQL,
          status: formGQL.form_by_pk.status,
          formAnswersMap,
          updateAnswers,
          refetchAnswers,
          selectedSchoolsSet,
          refetchSelectedSchools,
          questionsWithVerifiedAnswers,
        } satisfies FormContext);
      }
    );
  }, [
    formTemplateValue,
    formAnswersMap,
    formQuery.remoteData,
    formQuery.refetch,
    selectedSchoolsQuery.remoteData,
    selectedSchoolsQuery.refetch,
    updateAnswers,
  ]);

  return (
    <FormTemplateCtx.Provider value={formTemplateValue}>
      <FormCtx.Provider value={formValue}>
        <EligibilityServiceProvider
          formTemplateRD={formTemplateValue.map((value) => value.formTemplate)}
        >
          {children(formTemplateValue, formValue, formTemplateSettingsValue)}
        </EligibilityServiceProvider>
      </FormCtx.Provider>
    </FormTemplateCtx.Provider>
  );
};

export function getQuestionsWithVerifiedAnswers(
  formTemplateContext: FormTemplateContext,
  formGQL: GQL.GetFormProviderForm
): Set<uuid> {
  const questionsWithVerifiedAnswers = new Set<uuid>();
  const verificationMap = new Map(
    formGQL.form_verification_result.map((result) => [
      result.form_verification_id,
      result.verification_status,
    ])
  );
  const completeQuestions = Question.getFormCompleteQuestions(
    formTemplateContext.formTemplate
  );
  for (const question of completeQuestions) {
    if (!question.formVerification) {
      continue;
    }

    if (
      verificationMap.get(question.formVerification.id) ===
      GQL.verification_status_enum.Verified
    ) {
      questionsWithVerifiedAnswers.add(question.id);
    }
  }

  return questionsWithVerifiedAnswers;
}

function toAnswersMap(
  answers: GQL.GetFormAnswersById,
  formTemplate: AF.FormTemplate<AF.WithId>
): FormAnswersMap {
  const map = new Map();
  const allQuestions = formTemplate.sections.flatMap((s) => {
    if (
      s === undefined ||
      s.type === "SchoolRankingSection" ||
      s.type === "DisclaimerSection"
    ) {
      return [];
    }

    return s.questions;
  });
  getAllQuestionsAndAnswers(
    allQuestions,
    answers.form_answer,
    answers.grades_answer,
    answers.form_address,
    answers.custom_question_answer,
    []
  ).forEach(([q, a]) => {
    map.set(q.id, a);
  });

  return map;
}

export function useFormTemplate() {
  const context = useContext(FormTemplateCtx);
  if (context === undefined) {
    throw new Error("useFormTemplate must be used within a FormProvider");
  }

  if (!context.hasData()) {
    throw new Error("Missing data");
  }

  return context.data.formTemplate;
}

export function useFormTemplateContext() {
  const context = useContext(FormTemplateCtx);
  if (context === undefined) {
    throw new Error(
      "useFormTemplateContext must be used within a FormProvider"
    );
  }

  if (!context.hasData()) {
    throw new Error("Missing data");
  }

  return context.data;
}

export function useFormAnswers() {
  const context = useContext(FormCtx);

  if (context === undefined) {
    throw new Error("useFormAnswers must be used within a FormProvider");
  }

  if (!context.hasData()) {
    throw new Error("Missing data");
  }

  return context.data.formAnswersMap;
}

export function useFormContext() {
  const context = useContext(FormCtx);

  if (context === undefined) {
    throw new Error("useFormContext must be used within a FormProvider");
  }

  if (!context.hasData()) {
    throw new Error("Missing data");
  }

  return context.data;
}

export function useVisibilityContext(): Visibility.VisibilityContext {
  const formTemplateContext = useFormTemplateContext();
  const formAnswersContext = useFormContext();

  const context = useMemo(() => {
    const formQuestions = getFormCompleteQuestions(
      formTemplateContext.formTemplate
    );
    const sortedQuestionIds = formQuestions.map((q) => q.id);
    const visibilityMap = Visibility.toVisibilityMap(formQuestions);

    return {
      sortedQuestionIds,
      visibilityMap,
      formAnswersMap: formAnswersContext.formAnswersMap,
      questionKeysMap: formTemplateContext.questionKeysMap,
      optionsMap: formTemplateContext.optionsMap,
      selectedSchoolsSet: formAnswersContext.selectedSchoolsSet,
      questionsWithVerifiedAnswers:
        formAnswersContext.questionsWithVerifiedAnswers,
    };
  }, [
    formAnswersContext.formAnswersMap,
    formAnswersContext.selectedSchoolsSet,
    formTemplateContext.formTemplate,
    formTemplateContext.optionsMap,
    formTemplateContext.questionKeysMap,
    formAnswersContext.questionsWithVerifiedAnswers,
  ]);

  return context;
}

export const FormWithAnswersTracker = forwardRef<PropsWithChildren, "form">(
  (props, ref) => {
    const { values, dirty } = useFormikContext<Record<string, Answer>>();
    const { updateAnswers } = useFormContext();

    useEffect(() => {
      updateAnswers(values);
    }, [values, updateAnswers, dirty]);

    return <Form ref={ref} {...props} />;
  }
);

function toError(apolloError: ApolloError): Error {
  return new Error("GraphQL error", { cause: apolloError });
}
